/**
 * Centralized error handling utility for the frontend
 * This module provides consistent error handling across the application
 */

/**
 * Error types enum
 */
export const ErrorTypes = {
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

/**
 * Default error messages by type
 */
const DEFAULT_ERROR_MESSAGES = {
  [ErrorTypes.BAD_REQUEST]: 'La solicitud no es válida. Por favor, verifica los datos enviados.',
  [ErrorTypes.UNAUTHORIZED]: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
  [ErrorTypes.FORBIDDEN]: 'No tienes permisos para realizar esta acción.',
  [ErrorTypes.NOT_FOUND]: 'El recurso solicitado no existe.',
  [ErrorTypes.CONFLICT]: 'Hay un conflicto con los datos enviados.',
  [ErrorTypes.VALIDATION_ERROR]: 'Hay errores de validación en los datos enviados.',
  [ErrorTypes.RATE_LIMIT_EXCEEDED]:
    'Has realizado demasiadas solicitudes. Por favor, espera un momento antes de intentarlo de nuevo.',
  [ErrorTypes.SERVER_ERROR]: 'Ha ocurrido un error en el servidor. Por favor, inténtalo más tarde.',
  [ErrorTypes.NETWORK_ERROR]: 'Error de conexión. Por favor, verifica tu conexión a internet.',
  [ErrorTypes.UNKNOWN_ERROR]: 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.',
};

/**
 * Creates a standardized error object
 * @param {string} type - Error type from ErrorTypes enum
 * @param {string} message - Error message
 * @param {Object} data - Additional error data
 * @param {Error} originalError - Original error object
 * @returns {Object} Standardized error object
 */
export const createError = (type, message, data = null, originalError = null) => {
  return {
    type,
    message:
      message || DEFAULT_ERROR_MESSAGES[type] || DEFAULT_ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
    data,
    originalError,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Processes an API error and returns a standardized error object
 * @param {Error} error - Error object from API call
 * @param {string} defaultMessage - Default error message
 * @returns {Object} Standardized error object
 */
export const processApiError = (
  error,
  defaultMessage = DEFAULT_ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
) => {
  // Si ya es un error estandarizado, devolverlo
  if (error && error.type && Object.values(ErrorTypes).includes(error.type)) {
    return error;
  }

  // Handle network errors
  if (error && error.message === 'Network Error') {
    return createError(
      ErrorTypes.NETWORK_ERROR,
      DEFAULT_ERROR_MESSAGES[ErrorTypes.NETWORK_ERROR],
      null,
      error,
    );
  }

  // Handle HTTP errors
  if (error && error.response) {
    const { status, data } = error.response;
    const errorMessage = (data && data.message) || defaultMessage;

    switch (status) {
      case 400:
        return createError(ErrorTypes.BAD_REQUEST, errorMessage, data, error);
      case 401:
        return createError(ErrorTypes.UNAUTHORIZED, errorMessage, data, error);
      case 403:
        return createError(ErrorTypes.FORBIDDEN, errorMessage, data, error);
      case 404:
        return createError(ErrorTypes.NOT_FOUND, errorMessage, data, error);
      case 409:
        return createError(ErrorTypes.CONFLICT, errorMessage, data, error);
      case 422:
        return createError(ErrorTypes.VALIDATION_ERROR, errorMessage, data, error);
      case 429:
        return createError(ErrorTypes.RATE_LIMIT_EXCEEDED, errorMessage, data, error);
      case 500:
      case 502:
      case 503:
        return createError(ErrorTypes.SERVER_ERROR, errorMessage, data, error);
      default:
        return createError(ErrorTypes.UNKNOWN_ERROR, errorMessage, data, error);
    }
  }

  // Handle any other errors
  return createError(
    ErrorTypes.UNKNOWN_ERROR,
    (error && error.message) || defaultMessage,
    null,
    error,
  );
};

/**
 * Gets a user-friendly error message from an error object
 * @param {Object|Error} error - Error object
 * @param {string} defaultMessage - Default error message
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = (
  error,
  defaultMessage = DEFAULT_ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
) => {
  // If it's a string, return it directly
  if (typeof error === 'string') {
    return error;
  }

  // If it's a standardized error, return its message
  if (error && error.type && error.message) {
    return error.message;
  }

  // If it's an API error with a response
  if (error && error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }

  // If it's a standard Error object
  if (error && error.message) {
    return error.message;
  }

  // Default case
  return defaultMessage;
};

export default {
  ErrorTypes,
  createError,
  processApiError,
  getUserFriendlyErrorMessage,
};
